import React, { createContext, useContext, useEffect, useState } from "react"
import { useIsMobile } from "src/hooks/use-is-mobile"

interface MobileConversationHeader {
  title: string
  onBack: () => void
}

interface NavigationContextType {
  isLeftNavCollapsed: boolean
  toggleLeftNav: () => void
  isMobile: boolean
  isLeftNavOpen: boolean
  setLeftNavOpen: (open: boolean) => void
  mobileConversationHeader: MobileConversationHeader | null
  setMobileConversationHeader: (header: MobileConversationHeader | null) => void
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined)

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile()
  const [isLeftNavCollapsed, setIsLeftNavCollapsed] = useState(() => {
    // Initialize from localStorage if available, default to false (expanded)
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("leftNavCollapsed")
      return saved ? JSON.parse(saved) : false
    }
    return false
  })
  const [isLeftNavOpen, setLeftNavOpen] = useState(false)
  const [mobileConversationHeader, setMobileConversationHeader] = useState<MobileConversationHeader | null>(null)

  const toggleLeftNav = () => {
    if (isMobile) {
      // On mobile, toggle the drawer open state
      setLeftNavOpen((prev) => !prev)
    } else {
      // On desktop, toggle collapsed state
      setIsLeftNavCollapsed((prev: boolean) => {
        const newValue = !prev
        if (typeof window !== "undefined") {
          localStorage.setItem("leftNavCollapsed", JSON.stringify(newValue))
        }
        return newValue
      })
    }
  }

  // Close mobile drawer when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setLeftNavOpen(false)
    }
  }, [isMobile])

  // Sync with localStorage on mount in case of SSR
  useEffect(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("leftNavCollapsed")
      if (saved !== null) {
        setIsLeftNavCollapsed(JSON.parse(saved))
      }
    }
  }, [])

  // Clear mobile conversation header when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setMobileConversationHeader(null)
    }
  }, [isMobile])

  return (
    <NavigationContext.Provider
      value={{
        isLeftNavCollapsed,
        toggleLeftNav,
        isMobile,
        isLeftNavOpen,
        setLeftNavOpen,
        mobileConversationHeader,
        setMobileConversationHeader,
      }}
    >
      {children}
    </NavigationContext.Provider>
  )
}

export function useNavigation() {
  const context = useContext(NavigationContext)
  if (context === undefined) {
    throw new Error("useNavigation must be used within a NavigationProvider")
  }
  return context
}